import { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Search, Tv, Loader2, Radio, AlertTriangle, Star, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUICK = ["All", "Sports", "News", "Movies", "Entertainment", "Music", "Kids", "Documentary"];

export default function LiveTVPanel({ open, onClose, onSelect, autoLoadUrl }) {
  const { account, authFetch } = useAuth();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [saved, setSaved] = useState([]);
  const [loadedUrl, setLoadedUrl] = useState("");
  const [savedAutoUrl, setSavedAutoUrl] = useState("");

  const load = useCallback(async (overrideUrl) => {
    const target = (overrideUrl ?? url).trim();
    if (!target) { toast.error("Paste an M3U playlist URL first"); return; }
    if (overrideUrl) setUrl(overrideUrl);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/iptv/parse`, { params: { url: target } });
      setData(res.data);
      setLoadedUrl(target);
      setCat("All");
      setQ("");
      toast.success(`Loaded ${res.data.count.toLocaleString()} channels`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load that playlist");
    } finally {
      setLoading(false);
    }
  }, [url]);

  const refreshSaved = useCallback(async () => {
    if (!account) { setSaved([]); return; }
    try { const d = await authFetch("/m3u/links"); setSaved(d.links || []); }
    catch (e) { console.debug("load saved m3u failed", e); }
  }, [account, authFetch]);

  // when the panel opens: load saved playlists + auto-load a playlist passed from the Service Grid
  useEffect(() => {
    if (!open) return;
    refreshSaved();
    if (autoLoadUrl && autoLoadUrl !== savedAutoUrl) {
      setSavedAutoUrl(autoLoadUrl);
      load(autoLoadUrl);
    }
  }, [open, autoLoadUrl, savedAutoUrl, refreshSaved, load]);

  const alreadySaved = useMemo(() => saved.some((s) => s.url === loadedUrl), [saved, loadedUrl]);

  const saveCurrent = async () => {
    if (!account) { toast("Log in to save playlists"); return; }
    if (!loadedUrl) return;
    try {
      const name = (loadedUrl.split("?")[0].split("/").pop()) || (data?.channels?.[0]?.group) || "Playlist";
      const d = await authFetch("/m3u/links", { method: "POST", body: JSON.stringify({ url: loadedUrl, name }) });
      setSaved(d.links || []);
      toast.success("Playlist saved to your account");
    } catch (e) { toast.error(e.message || "Couldn't save playlist"); }
  };

  const removeSaved = async (e, link) => {
    e.stopPropagation();
    try { const d = await authFetch("/m3u/links", { method: "DELETE", body: JSON.stringify({ url: link.url }) }); setSaved(d.links || []); }
    catch (err) { toast.error("Couldn't remove playlist"); }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    const catL = cat.toLowerCase();
    return data.channels.filter((c) => {
      const inCat = cat === "All" || (c.group || "").toLowerCase().includes(catL);
      const inSearch = !term || c.name.toLowerCase().includes(term) || (c.country || "").toLowerCase().includes(term) || (c.language || "").toLowerCase().includes(term) || (c.group || "").toLowerCase().includes(term);
      return inCat && inSearch;
    }).slice(0, 400);
  }, [data, cat, q]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="livetv-panel">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-[#0A0E27] border border-white/10 rounded-t-3xl sm:rounded-2xl max-h-[88vh] sm:max-h-[80vh] flex flex-col overflow-hidden shadow-2xl animate-in slide-in-from-bottom sm:zoom-in-95 duration-200">
        {/* header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <h2 className="flex items-center gap-2 text-lg font-bold text-[#F1F1F5]"><Radio className="w-5 h-5 text-[#00D9FF]" /> Live TV</h2>
          <button data-testid="livetv-close" onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full text-[#A0A0AB] hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>

        {/* M3U input */}
        <div className="p-4 border-b border-white/10 shrink-0 space-y-3">
          <div data-testid="m3u-legal-warning" className="flex items-start gap-2.5 rounded-xl p-2.5 text-[12px] leading-snug" style={{ background: "rgba(230,164,41,.1)", border: "1px solid rgba(230,164,41,.4)", color: "#F0D48A" }}>
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#E6A429" }} />
            <span><strong className="text-[#E6A429]">Only legal streams please.</strong> Using pirated IPTV is illegal. Make sure your M3U link is legal — Pluto TV, free/public broadcasts, or your own hosted streams.</span>
          </div>
          <div className="flex gap-2">
            <input
              data-testid="m3u-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Paste M3U / M3U8 playlist URL"
              className="flex-1 h-11 bg-[#0F1636] border border-white/10 rounded-xl px-3 text-[15px] text-[#F1F1F5] outline-none focus:border-[#00D9FF] placeholder:text-[#71717A]"
            />
            <button
              data-testid="m3u-load-button"
              onClick={() => load()}
              disabled={loading}
              className="h-11 px-4 bg-[#00D9FF] text-navy-ink font-semibold rounded-xl hover:bg-[#33E4FF] transition-colors disabled:opacity-60 flex items-center gap-2 shrink-0"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Load"}
            </button>
          </div>

          {/* saved playlists */}
          {account && saved.length > 0 && (
            <div data-testid="saved-playlists">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#71717A] mb-1.5">Saved playlists</p>
              <div className="flex flex-col gap-1.5 max-h-28 overflow-y-auto">
                {saved.map((s) => (
                  <div key={s.url} className="flex items-center gap-2 bg-[#0F1636] border border-white/10 rounded-lg pl-3 pr-1.5 h-9">
                    <button data-testid="saved-playlist-load" onClick={() => load(s.url)} className="flex-1 min-w-0 text-left text-[13px] text-[#F1F1F5] truncate hover:text-[#00D9FF]">{s.name || s.url}</button>
                    <button data-testid="saved-playlist-delete" onClick={(e) => removeSaved(e, s)} title="Remove" className="w-7 h-7 flex items-center justify-center rounded-md text-[#71717A] hover:text-[#FF3B5C]"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {data && (
          <>
            {/* categories + search + save */}
            <div className="px-4 pt-3 shrink-0 space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 flex-1 h-10 bg-[#0F1636] border border-white/10 rounded-xl px-3 focus-within:border-[#00D9FF]">
                  <Search className="w-4 h-4 text-[#71717A]" />
                  <input data-testid="channel-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search channels, country, language…" className="flex-1 bg-transparent outline-none text-[15px] text-[#F1F1F5] placeholder:text-[#71717A]" />
                </div>
                {account && (
                  <button
                    data-testid="save-playlist-button"
                    onClick={saveCurrent}
                    disabled={alreadySaved}
                    title={alreadySaved ? "Already saved" : "Save this playlist to your account"}
                    className={`h-10 px-3 rounded-xl flex items-center gap-1.5 text-xs font-semibold shrink-0 transition-colors ${alreadySaved ? "bg-[#0F1636] text-[#00FF66] border border-[#00FF66]/40" : "bg-[#00FF66] text-navy-ink hover:bg-[#33FF85]"}`}
                  >
                    <Star className={`w-3.5 h-3.5 ${alreadySaved ? "fill-current" : ""}`} /> {alreadySaved ? "Saved" : "Save"}
                  </button>
                )}
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                {QUICK.map((c) => (
                  <button
                    key={c}
                    data-testid={`cat-${c}`}
                    onClick={() => setCat(c)}
                    className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold transition-colors ${cat === c ? "bg-[#00D9FF] text-navy-ink" : "bg-[#0F1636] text-[#A0A0AB] border border-white/10"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* channel list */}
            <div className="overflow-y-auto flex-1 mt-2" data-testid="channel-list" style={{ WebkitOverflowScrolling: "touch" }}>
              {filtered.length === 0 ? (
                <p className="text-center text-sm text-[#71717A] py-10">No channels match your filters.</p>
              ) : (
                filtered.map((c, i) => (
                  <button
                    key={`${c.url}-${i}`}
                    data-testid="channel-item"
                    onClick={() => { onSelect(c); onClose(); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/10 active:bg-white/15 transition-colors border-b border-white/5"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#0F1636] border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                      {c.logo ? <img src={c.logo} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; }} /> : <Tv className="w-5 h-5 text-[#71717A]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#F1F1F5] text-sm font-medium truncate">{c.name}</p>
                      <p className="text-[11px] text-[#71717A] truncate">{[c.group, c.country, c.language].filter(Boolean).join(" · ") || "Live"}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
